<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { Icons } from '@/models/icons.enum';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import InfoCardContainer from '../InfoCardContainer.vue';

const props = withDefaults(
  defineProps<{
    data: NonNullable<ResolvedSelfSellInModel['catalogDownloadSection']>['catalogs'][number];
    id: string;
    loading: boolean;
  }>(),
  {
    loading: false,
  },
);

const emit = defineEmits<{
  download: [catalog: NonNullable<ResolvedSelfSellInModel['catalogDownloadSection']>['catalogs'][number]];
}>();

const { t } = useI18n();

const icon = computed(() => (props.loading ? Icons.LOADING : Icons.DOWNLOAD_ARROW_SIGN));
const titleParts = computed(() => props.data.title.split('/'));
const resolvedTitle = computed(() => titleParts.value[0]?.trim() ?? '');
const resolvedSubtitle = computed(() => titleParts.value[1]?.trim() ?? '');

const buttonLabel = computed(() => {
  if (props.loading) {
    return t('viewSelfSellIn.catalog_download.button_downloading');
  }

  return t('global.download_button');
});
</script>

<template>
  <InfoCardContainer class="catalog-card" height="tall" :background-image-url="data.imageUrl" :id="id">
    <div class="card-header">
      <h3 class="card-header__title">{{ resolvedTitle }}</h3>
      <p class="card-header__subtitle">{{ resolvedSubtitle }}</p>
    </div>
    <BackstageButton
      class="download-button"
      @click="emit('download', data)"
      :icon-right="icon"
      :disabled="loading"
      data-cy-button="catalog-card-download-button"
      >{{ buttonLabel }}</BackstageButton
    >
  </InfoCardContainer>
</template>

<style lang="scss" scoped>
.catalog-card {
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
    pointer-events: none;
  }
}
.card-header {
  z-index: 1;

  &__title,
  &__subtitle {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.48px;
  }
}

.download-button {
  align-self: flex-end;
  z-index: 1;

  // BackstageButton dims the whole button to opacity 0.2 when :disabled.
  // Override so the loading spinner stays fully visible.
  :deep(button:disabled) {
    opacity: 0.7;
  }

  :deep(.loading) {
    background-color: var(--secondary-text-color);
  }
}
</style>
